import type { UpdateCurrentUserRequest, UserSummary } from '@compass/client/types/user';
import { membershipService, userService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';

export const useCurrentUser = (organizationId?: string, programId?: string) => {
  return useQuery({
    queryKey: queryKeys.users.me(organizationId, programId),
    queryFn: () => userService.getCurrentUser({}),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useUpdateCurrentUser = () => {
  const queryClient = useQueryClient();
  const setUser = useStore(state => state.setUser);
  const { selectedOrganizationId, selectedProgramId } = useStore();

  return useMutation({
    mutationFn: (data: UpdateCurrentUserRequest) => userService.updateCurrentUser(data),
    onSuccess: response => {
      if (response.user) {
        // Update the cache immediately with the response data for current org/program
        queryClient.setQueryData(
          queryKeys.users.me(selectedOrganizationId ?? undefined, selectedProgramId ?? undefined),
          response,
        );
        setUser(response.user);
      }
      toast.success('Profile updated successfully');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      toast.error(errorMessage);
    },
  });
};

export const useCurrentUserRoles = (organizationId?: string, programId?: string) => {
  const setUserRoles = useStore(state => state.setUserRoles);

  return useQuery({
    queryKey: [...queryKeys.users.me(), 'roles', organizationId, programId],
    queryFn: async () => {
      const response = await membershipService.getMyRoles({});
      // Store roles in Zustand store for global access
      if (response.user_roles) {
        setUserRoles(response.user_roles);
      }
      return response;
    },
    enabled: !!organizationId && !!programId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// The current user's roles across all orgs, not gated on an org/program being
// selected (unlike useCurrentUserRoles).
export const useMyRoles = () =>
  useQuery({
    queryKey: [...queryKeys.users.me(), 'roles', 'unscoped'],
    queryFn: () => membershipService.getMyRoles({}),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

// Hook to lookup users by IDs using the lightweight LookupUsers API.
// Only requires authentication — no org-level permissions needed.
// Returns a map of userId -> User for easy lookup.
export const useUsersByIds = (userIds: string[], enabled = true) => {
  // Deduplicate and filter empty IDs
  const uniqueUserIds = useMemo(() => {
    return [...new Set(userIds.filter(id => id && id.trim()))];
  }, [userIds]);

  const query = useQuery({
    queryKey: ['users', 'byIds', uniqueUserIds],
    queryFn: () =>
      userService.lookupUsers({
        user_ids: uniqueUserIds,
      }),
    enabled: enabled && uniqueUserIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Create a map for easy lookup (includes availability from UserSummary)
  const usersMap = useMemo(() => {
    const map = new Map<string, UserSummary>();
    if (query.data?.users) {
      for (const user of query.data.users) {
        if (user.id) {
          map.set(user.id, {
            id: user.id,
            email: user.email,
            name: user.name,
            available: user.available,
          });
        }
      }
    }
    return map;
  }, [query.data?.users]);

  return {
    ...query,
    usersMap,
  };
};
